/**
 * Correcting an animal after it is registered (P4-T34, BR-12).
 *
 * A wrong tag, sex, age, mother or price used to stay wrong forever. This form
 * is prefilled from the profile and sends only what changed, so the audit row
 * says what was corrected rather than that every field was rewritten.
 *
 * Species is not offered: the tag prefix, the mother and the vaccine schedule
 * all follow from it, so a goat entered as cattle is re-registered. The price
 * is not offered for an animal bought on a purchase trip — its price is a
 * share of the trip's expense (P2-BR2) — nor for one born on the farm, which
 * has none. The server refuses all three regardless; hiding them only avoids a
 * field that cannot be saved.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { todayInDhaka } from '../../lib/date.js';
import { uploadReceipt as uploadPhoto } from '../finance/api.js';
import { MAX_AGE_MONTHS, ageMonthsFromDays, dobFromAgeMonths } from './animalAge.js';
import { useAnimalProfile, useAnimals, useUpdateAnimal, type AnimalChanges, type AnimalProfile } from './api.js';

/** Digits with at most two decimals, checked as text — never parsed (MONEY-1). */
const DECIMAL_DRAFT = /^\d{1,12}(\.\d{1,2})?$/;

export function AnimalEditForm(): JSX.Element {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const { data: animal, isLoading, isError } = useAnimalProfile(Number(params.id));

  if (isLoading) {
    return (
      <p role="status" className="py-8 text-center text-gray-500">
        {t('app.loading')}
      </p>
    );
  }

  if (isError || !animal) {
    return (
      <p role="alert" className="mx-auto max-w-md px-4 py-8 text-sm text-red-800">
        {t('error.network')}
      </p>
    );
  }

  // Keyed so a refetch after saving starts the form from the saved values.
  return <EditForm key={`${String(animal.id)}-${animal.tag}`} animal={animal} />;
}

function EditForm({ animal }: { animal: AnimalProfile }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const update = useUpdateAnimal();

  const initialAge = ageMonthsFromDays(animal.ageDays);
  const initialAgeText = initialAge !== null ? String(initialAge) : '';

  const [tag, setTag] = useState(animal.tag);
  const [sex, setSex] = useState(animal.sex);
  const [breed, setBreed] = useState(animal.breed ?? '');
  const [ageMonths, setAgeMonths] = useState(initialAgeText);
  const [motherId, setMotherId] = useState<number | null>(animal.motherId);
  const [purchaseDate, setPurchaseDate] = useState(animal.purchaseDate ?? '');
  const [purchasePrice, setPurchasePrice] = useState(animal.purchasePrice ?? '');
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // A trip-bought animal's price belongs to the trip; a farm-born one has none.
  const priceEditable = animal.source === 'purchased' && animal.purchaseTxnId === null;

  const { data: herd } = useAnimals({ species: animal.species, sex: 'female', limit: 100 });
  // Not the animal itself. The server also refuses its own offspring.
  const mothers = (herd?.items ?? []).filter((candidate) => candidate.id !== animal.id);

  const ageChanged = ageMonths.trim() !== initialAgeText;
  const dobEstimate = ageChanged ? dobFromAgeMonths(ageMonths) : null;
  const ageIsUsable = !ageChanged || dobEstimate !== null;

  const changes: AnimalChanges = {
    ...(tag.trim().toUpperCase() !== animal.tag ? { tag: tag.trim().toUpperCase() } : {}),
    ...(sex !== animal.sex ? { sex } : {}),
    ...(breed.trim() !== (animal.breed ?? '') && breed.trim() !== '' ? { breed: breed.trim() } : {}),
    ...(dobEstimate !== null ? { dobEstimate } : {}),
    ...(motherId !== animal.motherId ? { motherId } : {}),
    ...(priceEditable && purchaseDate !== (animal.purchaseDate ?? '') ? { purchaseDate } : {}),
    ...(priceEditable && purchasePrice !== (animal.purchasePrice ?? '') ? { purchasePrice } : {}),
  };

  const priceIsUsable = !priceEditable || DECIMAL_DRAFT.test(purchasePrice);
  const changed = Object.keys(changes).length > 0 || photo !== null;
  const canSubmit = changed && ageIsUsable && priceIsUsable && !busy;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setFieldErrors({});
    setBusy(true);

    try {
      const photoUrl = photo !== null ? await uploadPhoto(photo) : undefined;
      await update.mutateAsync({
        id: animal.id,
        changes: { ...changes, ...(photoUrl !== undefined ? { photoUrl } : {}) },
      });
      void navigate(`/animals/${String(animal.id)}`);
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    } finally {
      setBusy(false);
    }
  }

  const label = 'mb-1 mt-3 block text-sm font-medium text-gray-700';
  const input = 'min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3';
  const fieldError = (name: string): JSX.Element | null =>
    fieldErrors[name] !== undefined ? <p className="mt-1 text-sm text-red-700">{fieldErrors[name]}</p> : null;

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('animals.edit.title', { tag: animal.tag })}</h1>

      <form onSubmit={(event) => void handleSubmit(event)} noValidate aria-label={t('animals.edit.form')}>
        <p className="rounded-lg bg-gray-100 px-4 py-3 text-xs text-gray-600">
          {t('animals.edit.speciesFixed', { species: t(`animals.species.${animal.species}`) })}
        </p>

        <label className={label} htmlFor="edit-tag">
          {t('animals.add.tag')}
        </label>
        <input
          id="edit-tag"
          value={tag}
          onChange={(event) => setTag(event.target.value)}
          className={`${input} uppercase`}
        />
        {fieldError('tag')}

        <label className={label} htmlFor="edit-sex">
          {t('animals.sexLabel')}
        </label>
        <select
          id="edit-sex"
          value={sex}
          onChange={(event) => setSex(event.target.value as 'male' | 'female')}
          className={input}
        >
          {(['female', 'male'] as const).map((option) => (
            <option key={option} value={option}>
              {t(`animals.sex.${option}`)}
            </option>
          ))}
        </select>
        {fieldError('sex')}

        <label className={label} htmlFor="edit-breed">
          {t('animals.add.breed')}
        </label>
        <input id="edit-breed" value={breed} onChange={(event) => setBreed(event.target.value)} className={input} />
        {fieldError('breed')}

        <label className={label} htmlFor="edit-age">
          {t('animals.add.ageMonths')}
        </label>
        <input
          id="edit-age"
          inputMode="numeric"
          value={ageMonths}
          onChange={(event) => setAgeMonths(event.target.value)}
          max={MAX_AGE_MONTHS}
          className={`${input} tabular-nums`}
        />
        {ageChanged && (
          <p className="mt-1 text-xs text-gray-500" aria-live="polite">
            {dobEstimate !== null ? t('animals.add.ageDerived', { date: dobEstimate }) : t('animals.add.ageInvalid')}
          </p>
        )}
        {fieldError('dobEstimate')}

        <label className={label} htmlFor="edit-mother">
          {t('animals.add.mother')}
        </label>
        <select
          id="edit-mother"
          value={motherId === null ? '' : String(motherId)}
          onChange={(event) => setMotherId(event.target.value === '' ? null : Number(event.target.value))}
          className={input}
        >
          <option value="">{t('animals.add.motherNone')}</option>
          {mothers.map((mother) => (
            <option key={mother.id} value={mother.id}>
              {mother.tag}
              {mother.breed !== null ? ` · ${mother.breed}` : ''}
            </option>
          ))}
        </select>
        {fieldError('motherId')}

        {priceEditable ? (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="edit-purchase-date">
                {t('animals.add.purchaseDate')}
              </label>
              <input
                id="edit-purchase-date"
                type="date"
                value={purchaseDate}
                max={todayInDhaka()}
                onChange={(event) => setPurchaseDate(event.target.value)}
                className={input}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="edit-purchase-price">
                {t('animals.add.purchasePrice')}
              </label>
              <input
                id="edit-purchase-price"
                inputMode="decimal"
                value={purchasePrice}
                onChange={(event) => setPurchasePrice(event.target.value)}
                className={`${input} tabular-nums`}
              />
            </div>
          </div>
        ) : (
          <p className="mt-3 rounded-lg bg-gray-100 px-4 py-3 text-xs text-gray-600">
            {t(animal.source === 'born' ? 'animals.add.bornNote' : 'animals.edit.priceFromTrip')}
          </p>
        )}
        {fieldError('purchasePrice')}
        {fieldError('purchaseDate')}

        <label className={label} htmlFor="edit-photo">
          {t(animal.photoUrl !== null ? 'animals.edit.replacePhoto' : 'animals.edit.addPhoto')}
        </label>
        <input
          id="edit-photo"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          onChange={(event) => setPhoto(event.target.files?.[0] ?? null)}
          className="w-full text-sm"
        />
        {fieldError('photoUrl')}

        {errorKey !== null && (
          <div role="alert" className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
            <p>{t(errorKey)}</p>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="submit"
            disabled={!canSubmit}
            className="min-h-[52px] flex-1 rounded-xl bg-green-700 text-base font-medium text-white disabled:opacity-50"
          >
            {t(busy ? 'animals.add.saving' : 'animals.edit.save')}
          </button>
          <button
            type="button"
            onClick={() => void navigate(`/animals/${String(animal.id)}`)}
            className="min-h-touch rounded-xl bg-white px-4 py-2 text-sm text-gray-700"
          >
            {t('app.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
